import {Form, Modal} from 'antd';
import {
  AccountFieldsFragment,
  AccountStatus,
  StatusReason,
  useUpdateAccountMutation
} from 'api/generated';
import {Formik} from 'formik';
import {FormItem, Input, Mentions, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {useAdminUsers} from 'hooks/useAdminUsers';
import {SUSPENDED_REASONS} from 'lib/enums';
import {statusReasonLabels} from 'lib/labels';
import {getLanguageName} from 'lib/languages';
import {omitEmpty} from 'lib/omitEmpty';
import React from 'react';
import * as yup from 'yup';

type Props = {
  account?: AccountFieldsFragment | null;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

const {TextArea} = Input;

const validationSchema = yup.object().shape({
  statusReason: yup.string().required('Reason is required'),
  statusNote: yup.string().when('statusReason', {
    is: StatusReason.OTHER,
    then: yup.string().required('Note is required when reason is not specified'),
    otherwise: yup.string().nullable()
  })
});

export const SuspendAccountModal: React.FC<Props> = ({account, isOpen, setIsOpen}) => {
  const adminUsers = useAdminUsers();

  const [updateAccount] = useUpdateAccountMutation();

  const initialValues = {
    statusReason: account?.statusReason,
    statusNote: account?.statusNote,
    note: ''
  };

  const handleSubmit = async (
    {statusReason, statusNote, note}: typeof initialValues,
    {setSubmitting}: FormikHelpers<typeof initialValues>
  ) => {
    if (!account) return;
    try {
      await updateAccount({
        variables: {
          input: omitEmpty({
            id: account.id,
            status: AccountStatus.SUSPENDED,
            statusReason,
            statusNote,
            note
          })
        }
      });
      setIsOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({submitForm, isSubmitting, resetForm}) => (
          <Modal
            title="Suspend account"
            okText="Suspend"
            okType="danger"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            width={640}
            afterClose={resetForm}
            onCancel={() => {
              setIsOpen(false);
            }}>
            <Form layout="vertical">
              <p>Permanently suspend the merchant's account.</p>
              <FormItem name="statusReason" label="Reason" hasFeedback={false}>
                <Select
                  name="statusReason"
                  placeholder="Select the reason"
                  options={SUSPENDED_REASONS.map((key) => ({
                    value: key,
                    label: statusReasonLabels[key]
                  }))}
                />
              </FormItem>
              <FormItem
                name="statusNote"
                label={`Merchant note in ${getLanguageName(account?.language)}`}
                extra="This message will be shown to the user."
                hasFeedback={false}>
                <TextArea name="statusNote" rows={5} />
              </FormItem>
              <FormItem
                name="note"
                label="Admin note"
                extra="This message is for internal use only."
                hasFeedback={false}>
                <Mentions
                  rows={5}
                  name="note"
                  options={adminUsers}
                  filterOption={(input, option) =>
                    option.key!.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="You can use @ to mention other admin users"
                />
              </FormItem>
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
